/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2022, Zumper
 * @description   useImagePreload.tsx
 */
import { useCallback, useState } from 'react'

// Index 0 should be set to loading=eager
// When on index 0, a carousel slide forward will load the last image and image at index current + 1
// When on index 0, a carousel slide backward will load the last image and image at index current - 1
// Otherwise, this will load the next index going forward, or the previous index going backward
export const useImagePreload = (imgUrls: string[]) => {
  const [loadedIndexes, setLoadedIndexes] = useState<number[]>([0])

  // biome-ignore lint/correctness/useExhaustiveDependencies: biome wants more specific deps than we can actual give
  return useCallback(
    (previousIndex: number, currentIndex: number) => {
      const lastIndex = Math.max(imgUrls.length - 1, 0)

      let indexToPreload = 0
      if (previousIndex < currentIndex && currentIndex < lastIndex) {
        // Forward
        indexToPreload = currentIndex + 1
      } else if (imgUrls.length > 1 && previousIndex === currentIndex && currentIndex === 0) {
        // initial load: preload the next image
        indexToPreload = 1
      } else {
        // Backward
        indexToPreload = currentIndex === 0 ? lastIndex : currentIndex - 1
      }

      if (loadedIndexes.includes(indexToPreload)) {
        return
      }

      new Image().src = imgUrls[indexToPreload]
      setLoadedIndexes([...loadedIndexes, indexToPreload])
    },
    [loadedIndexes, setLoadedIndexes, imgUrls]
  )
}
